/**
 * Render Model Builder — browser-safe.
 *
 * Builds a `MigrationReportModel` (the contract in
 * src/types/render-model.ts) from the raw GeoEvent configuration, the
 * mapping result, and the generated Velocity configuration.
 *
 * This Phase 3b implementation produces a MINIMAL but schema-valid
 * model: title, summary counts, an executive summary section, a
 * component-mapping table per category, and warnings. The richer
 * per-service narrative produced by `src/docs/document-builder.ts`
 * (~2000 lines, see `buildMigrationDocument`) will be ported in
 * subsequent sub-phases as the browser pipeline reaches parity with the
 * legacy /api/migrate output. The interactive HTML report (Phase 3c)
 * is the primary rendering, so iterative section additions land
 * there first and propagate to the PDF/DOCX via this builder.
 */

import type { GeoEventConfiguration } from '../../types/geoevent.js';
import type { ConfigurationMappingResult, MappingResult } from '../../types/mapping.js';
import type { TargetPlatform, VelocityConfiguration } from '../../types/velocity.js';
import { formatTargetPlatformLabel } from '../../types/velocity.js';
import type { MigrationStrategy } from '../../types/api.js';
import {
  RENDER_MODEL_SCHEMA_VERSION,
  MAX_CELL_TEXT,
  MAX_SECTION_BODY_TEXT,
  MAX_WARNING_TEXT,
  migrationReportModelSchema,
  type MigrationReportModel,
  type ProbeReasonCode,
  type ReportSection,
  type ReportTable,
  type WarningRow,
} from '../../types/render-model.js';
import type { ProbeTargetResult } from './probe/orchestrator.js';
import { probeOutcomeMessage } from './probe/types.js';

export interface BuildRenderModelInput {
  geoEventConfig: GeoEventConfiguration;
  mappingResult: ConfigurationMappingResult;
  velocityConfig: VelocityConfiguration | null;
  targetPlatform: TargetPlatform;
  migrationStrategy: MigrationStrategy;
  sourceFilenameDisplay?: string;
  generatedAtIso?: string;
  warnings?: string[];
  errors?: string[];
  /**
   * Optional public-access probe results (§7.8). When provided, the
   * builder emits one warning row per non-`verified-public` result and
   * the HTML report renders a grouped "Public-access checks" section.
   */
  probeResults?: ProbeTargetResult[];
}

/**
 * Builds a schema-valid `MigrationReportModel`. Throws if the produced
 * model fails the zod schema — that indicates a builder bug and should
 * never reach a caller.
 */
export function buildRenderModel(input: BuildRenderModelInput): MigrationReportModel {
  const generatedAtIso = input.generatedAtIso ?? new Date().toISOString();
  const summary = summarize(input.mappingResult);
  const sections: ReportSection[] = [
    buildExecutiveSummarySection(input, summary),
    buildMappingDetailsSection(input.mappingResult),
  ];
  if (input.probeResults && input.probeResults.length > 0) {
    sections.push(buildProbeSection(input.probeResults));
  }
  const warnings = buildWarningRows(input);

  const model: MigrationReportModel = {
    schemaVersion: RENDER_MODEL_SCHEMA_VERSION,
    title: clip('ArcGIS Velocity Migration Guide', 400),
    generatedAtIso,
    sourceFilenameDisplay: input.sourceFilenameDisplay
      ? clipPlain(input.sourceFilenameDisplay, 255)
      : undefined,
    targetPlatform: input.targetPlatform,
    migrationStrategy: input.migrationStrategy,
    summary,
    sections,
    warnings,
  };

  // Defense-in-depth: validate before returning so any builder bug surfaces
  // immediately rather than blowing up later at the /api/render boundary.
  const parsed = migrationReportModelSchema.safeParse(model);
  if (!parsed.success) {
    const issues = parsed.error.issues
      .slice(0, 5)
      .map((i) => `${i.path.join('.') || '<root>'}: ${i.message}`)
      .join('; ');
    throw new Error(`buildRenderModel: produced model failed schema validation: ${issues}`);
  }
  return parsed.data;
}

export function summarize(mappingResult: ConfigurationMappingResult): MigrationReportModel['summary'] {
  let full = 0;
  let partial = 0;
  let unmapped = 0;
  let manual = 0;
  for (const m of mappingResult.mappings) {
    if (m.status === 'mapped') full++;
    else if (m.status === 'partial') partial++;
    else unmapped++; // 'unmapped' or 'manual-required'
    if (m.userReviewRequired) manual++;
  }
  return {
    totalComponents: mappingResult.mappings.length,
    fullyMapped: full,
    partiallyMapped: partial,
    unmapped,
    manualActions: manual,
  };
}

function buildExecutiveSummarySection(
  input: BuildRenderModelInput,
  summary: MigrationReportModel['summary'],
): ReportSection {
  const lines = [
    `Target platform: ${formatTargetPlatformLabel(input.targetPlatform)}.`,
    `Migration strategy: ${input.migrationStrategy}.`,
    `Components analyzed: ${summary.totalComponents}.`,
    `Fully mapped: ${summary.fullyMapped}. Partially mapped: ${summary.partiallyMapped}. No equivalent: ${summary.unmapped}.`,
    `Manual review required for ${summary.manualActions} component(s).`,
  ];
  if (input.velocityConfig) {
    const feeds = input.velocityConfig.feeds?.length ?? 0;
    const analytics = input.velocityConfig.realtime?.length ?? 0;
    lines.push(`Generated Velocity configuration contains ${feeds} feed(s) and ${analytics} real-time analytic(s).`);
  } else {
    lines.push('No Velocity JSON was generated because no fully compatible components were detected.');
  }
  return {
    title: 'Executive Summary',
    anchorId: 'executive-summary',
    content: clipPlain(lines.join('\n'), MAX_SECTION_BODY_TEXT),
  };
}

function buildMappingDetailsSection(mappingResult: ConfigurationMappingResult): ReportSection {
  const groups: Array<{ label: string; rows: MappingResult[] }> = [
    { label: 'Fully Compatible', rows: mappingResult.mappings.filter((m) => m.status === 'mapped') },
    { label: 'Partially Compatible', rows: mappingResult.mappings.filter((m) => m.status === 'partial') },
    { label: 'No Equivalent', rows: mappingResult.mappings.filter((m) => m.status === 'unmapped' || m.status === 'manual-required') },
  ];
  const tables: ReportTable[] = groups
    .filter((g) => g.rows.length > 0)
    .map((g) => ({
      title: clipPlain(g.label, 200),
      rows: g.rows.map((m) => ({
        parameter: clipPlain(componentDisplayName(m), MAX_CELL_TEXT),
        value: clipPlain(m.mapping?.velocityComponent ?? 'No Velocity equivalent', MAX_CELL_TEXT),
      })),
    }));

  return {
    title: 'Component Mapping',
    anchorId: 'component-mapping',
    content: clipPlain(
      'Each GeoEvent component is grouped by its mapping outcome. Partially compatible components require manual review of the noted differences before import.',
      MAX_SECTION_BODY_TEXT,
    ),
    tables,
  };
}

export function buildWarningRows(input: BuildRenderModelInput): WarningRow[] {
  const out: WarningRow[] = [];
  for (const m of input.mappingResult.mappings) {
    if (m.status === 'unmapped' || m.status === 'manual-required') {
      out.push({
        severity: 'unmapped',
        componentRef: clipPlain(componentDisplayName(m), 400),
        message: clipPlain(m.explanation || 'No Velocity equivalent available.', MAX_WARNING_TEXT),
      });
    } else if (m.userReviewRequired) {
      out.push({
        severity: 'manual-action',
        componentRef: clipPlain(componentDisplayName(m), 400),
        message: clipPlain(m.explanation || 'Manual review required.', MAX_WARNING_TEXT),
      });
    }
  }
  for (const w of input.warnings ?? []) {
    out.push({
      severity: 'warning',
      componentRef: 'configuration',
      message: clipPlain(w, MAX_WARNING_TEXT),
    });
  }
  for (const e of input.errors ?? []) {
    out.push({
      severity: 'manual-action',
      componentRef: 'configuration',
      message: clipPlain(e, MAX_WARNING_TEXT),
    });
  }
  for (const probe of input.probeResults ?? []) {
    if (probe.outcome === 'verified-public') continue;
    out.push({
      severity: probeSeverity(probe.outcome),
      componentRef: clipPlain(
        probe.componentRefs.length > 0 ? probe.componentRefs.join(', ') : probe.url,
        400,
      ),
      message: clipPlain(`${probeOutcomeMessage(probe.outcome)} (${probe.url})`, MAX_WARNING_TEXT),
      probeReasonCode: probe.outcome as ProbeReasonCode,
    });
  }
  // Hard cap to match schema (2000); legacy callers can drop excess.
  return out.slice(0, 2000);
}

/**
 * Maps a probe outcome to the warning severity used in the report.
 * `probe-disabled` is informational; everything else that is not
 * `verified-public` is a manual-action prompt because the migrator
 * cannot prove Velocity will be able to read the item.
 */
function probeSeverity(outcome: ProbeTargetResult['outcome']): WarningRow['severity'] {
  if (outcome === 'probe-disabled') return 'info';
  return 'manual-action';
}

function buildProbeSection(results: ProbeTargetResult[]): ReportSection {
  // Group by outcome; preserve §7.8 enum order for stable rendering.
  const ORDER: ProbeTargetResult['outcome'][] = [
    'verified-public',
    'requires-authentication',
    'not-found',
    'mixed-content-blocked',
    'internal-or-unroutable-host',
    'network-or-cors-blocked',
    'timeout',
    'unexpected-response',
    'probe-disabled',
  ];
  const groups = new Map<ProbeTargetResult['outcome'], ProbeTargetResult[]>();
  for (const r of results) {
    const list = groups.get(r.outcome) ?? [];
    list.push(r);
    groups.set(r.outcome, list);
  }
  const tables: ReportTable[] = [];
  for (const outcome of ORDER) {
    const rows = groups.get(outcome);
    if (!rows || rows.length === 0) continue;
    tables.push({
      title: clipPlain(`${probeGroupLabel(outcome)} (${rows.length})`, 200),
      rows: rows.map((r) => ({
        parameter: clipPlain(r.url, MAX_CELL_TEXT),
        value: clipPlain(
          r.componentRefs.length > 0 ? r.componentRefs.join(', ') : '—',
          MAX_CELL_TEXT,
        ),
      })),
    });
  }
  return {
    title: 'Public-Access Checks',
    anchorId: 'public-access-checks',
    content: clipPlain(
      'The assistant attempted an anonymous, unauthenticated check of each customer-facing endpoint referenced by the configuration. Per the strict success criterion, only endpoints returning a verified-public ArcGIS REST response are considered reachable by Velocity. Every other outcome requires manual confirmation; the rows below are grouped by cause so the appropriate remediation is clear.',
      MAX_SECTION_BODY_TEXT,
    ),
    tables,
  };
}

function probeGroupLabel(outcome: ProbeTargetResult['outcome']): string {
  switch (outcome) {
    case 'verified-public':
      return 'Verified anonymously reachable';
    case 'requires-authentication':
      return 'Requires authentication';
    case 'not-found':
      return 'Not found';
    case 'mixed-content-blocked':
      return 'Blocked by mixed-content policy';
    case 'internal-or-unroutable-host':
      return 'Internal or unroutable host';
    case 'network-or-cors-blocked':
      return 'Network or CORS blocked';
    case 'timeout':
      return 'Timed out';
    case 'unexpected-response':
      return 'Unexpected response';
    case 'probe-disabled':
      return 'Check skipped';
  }
}

function componentDisplayName(m: MappingResult): string {
  const c = m.component as { name?: string; label?: string };
  return c.label ?? c.name ?? 'component';
}

/**
 * Removes characters disallowed by the render-model `plainText` schema
 * (control chars + bidi overrides) and truncates to `maxLength`. The
 * server-side redaction sweep runs after this and is the authoritative
 * defense; this is here so the browser-side validator does not reject
 * upstream rough text that happens to contain a stray control character.
 */
function clipPlain(value: string, maxLength: number): string {
  // eslint-disable-next-line no-control-regex
  const cleaned = value.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F\u2066-\u2069\u202A-\u202E]/g, '');
  return clip(cleaned, maxLength);
}

function clip(value: string, maxLength: number): string {
  return value.length <= maxLength ? value : value.slice(0, maxLength);
}

// Phase 5: re-export the lightweight probe-attach helper so callers
// that already import from this module continue to work. The actual
// implementation lives in `probe-attach.ts` to keep the wizard bundle
// from pulling in the full builder graph (mapping/zod) when it only
// needs to graft probe results onto a model that was built elsewhere.
export { attachProbeResultsToModel } from './probe-attach.js';
